import React, { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import { Form } from "react-bootstrap";
import ViewMembersPanelNew from "../view-members-new/view-members-new";
import TaskDynamicFieldsInput from "../DynamicField/Tasks/Input/TasksDynamicFieldsInput";
import {
    FIELD_TYPES,
    isDynamicFieldValid,
    isMaskValid,
    isRegexValid
} from "../DynamicField/Tasks/utils/utils";
import ProgressPill from "../../pages/new-dashboard/dashboard-shared/progress-pill/progress-pill";
import { getLastUpdatedDateColor } from "../../pages/new-dashboard/dashboard-util/dashboard-utils";
import "../../pages/new-dashboard/dashboard-consumer-retail/dashboard-consumer-retail.scss"
import "./DynamicFieldBulk.scss";

const DynamicFieldBulkRow = ({
    data,
    row,
    hasProjectColumn,
    hasMemberColumn,
    hasDueDate,
    columnNamesNotNeeded,
    editMode,
    setSaveDisabled,
    setDynamicFieldsBulkfunc,
    isRealEstate,
}) => {
    const [dynamicFields, setDynamicFields] = useState(row.DynamicFields);
    const [note, setNote] = useState(row.Note);

    useEffect(() => {
        setDynamicFields([...row.DynamicFields]);
    }, [data]);

    const handleChange = (e, cell) => {
        let newFields = dynamicFields.map((oldField) =>
            oldField.Id == cell.Id
                ? {
                    ...oldField,
                    Data: e.target.value,
                    touched: true,
                }
                :
                oldField,
        );

        setDynamicFields(newFields);
        newFields = newFields.filter(x => !columnNamesNotNeeded.includes(x.Label));
        setDynamicFieldsBulkfunc([{ Id: row.Id, DynamicFields: newFields }]);
        setSaveDisabled(false);
    }

    const handleNoteChange = (note) => {
        setNote(note);
        setDynamicFieldsBulkfunc([{ Id: row.Id, DynamicFields: dynamicFields, Note: note }]);
        setSaveDisabled(false);
    };

    const DynamicFieldsFormInputFeedback = (props) => {
        const { field, setSaveDisabled } = props;

        if (!isMaskValid(field)) {
            setSaveDisabled(true);
            return 'Must follow the format: ' + field.FieldMask;
        } else if (!isRegexValid(field)) {
            setSaveDisabled(true);
            return field.FieldRegexDescription;
        } else if (!isDynamicFieldValid(field)) {
            setSaveDisabled(true);
            return field.FieldType === FIELD_TYPES.table
                ? 'Please add at least one row with values.'
                : 'Field is Required';
        } else {
            return null;
        }
    };

    return (
        <tr key={row.Id}>
            {   hasProjectColumn &&
                <td className="col-md-2 text-break">
                    {row.Project.Name}
                </td>
            }
            {   hasMemberColumn &&
                <td className="col-md-2 text-break pt-4">
                    <ViewMembersPanelNew
                        UserID={row.Project?.OwnerId}
                        members={row.Project?.Members}
                        projects={row.Project}
                        isHidden={true}
                    />
                </td>
            }
            {   hasDueDate &&
                <td className="col-md-1 col-sm-1 mt-2">
                    <ProgressPill start={row.Project?.Start} end={row.Project?.End} sortBy={"upcoming due dates"} />
                </td>
            }
            {   !editMode && row.DynamicFields.filter(x => {
                    if (!columnNamesNotNeeded.includes(x.Label)) return x;
                }).map(cell =>
                    <td key={cell.Id} className={`col-md-${isRealEstate ? "1" : "2 text-break"}`}>
                        <span>
                            {cell.Data}
                        </span>
                    </td>
                )
            }
            {   editMode && dynamicFields.filter(x => {
                    if (!columnNamesNotNeeded.includes(x.Label)) return x;
                }).map(cell =>
                    <td key={cell.Id || cell.Label} className={`col-md-${isRealEstate ? "1" : "2 text-break"}`}>
                        <TaskDynamicFieldsInput
                            field={cell}
                            handleChange={(e) => handleChange(e, cell)}
                            disableErrors={!cell.touched}
                            disabled={false}
                            setDynamicFields={setDynamicFields}
                        />
                        {   cell.touched &&
                            <Form.Control.Feedback type="invalid" className="d-block">
                                <DynamicFieldsFormInputFeedback field={cell} setSaveDisabled={setSaveDisabled} />
                            </Form.Control.Feedback>
                        }
                    </td>
                )
            }
            <td style={{ color: getLastUpdatedDateColor(row.LastUpdatedString)}}>
                {row.LastUpdatedString}
            </td>
            {   !editMode && isRealEstate &&
                <td className="col-md-2 text-break">
                   {note}
                </td>
            }
            {   editMode && isRealEstate && (
                <td className={`col-md-2"}`}>
                    <Form.Control
                        disabled={false}
                        type={'text'}
                        value={note}
                        onChange={(e) => handleNoteChange(e.target.value)}
                    />
                </td>
            )}
        </tr>
    );
};

DynamicFieldBulkRow.propTypes = {
    data: PropTypes.any,
    row: PropTypes.shape({
        Id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        DynamicFields: PropTypes.arrayOf(
            PropTypes.shape({
                Id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
                Label: PropTypes.string.isRequired,
                Data: PropTypes.any,
                touched: PropTypes.bool,
                FieldType: PropTypes.oneOf(Object.values(FIELD_TYPES)),
                FieldMask: PropTypes.string,
                FieldRegexDescription: PropTypes.string,
            })
        ).isRequired,
        Note: PropTypes.string,
        Project: PropTypes.shape({
            Name: PropTypes.string,
            OwnerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            Members: PropTypes.array,
            Start: PropTypes.string,
            End: PropTypes.string,
        }),
        LastUpdatedString: PropTypes.string,
    }).isRequired,
    hasProjectColumn: PropTypes.bool,
    hasMemberColumn: PropTypes.bool,
    hasDueDate: PropTypes.bool,
    columnNamesNotNeeded: PropTypes.arrayOf(PropTypes.string),
    editMode: PropTypes.bool,
    setSaveDisabled: PropTypes.func,
    setDynamicFieldsBulkfunc: PropTypes.func,
    isRealEstate: PropTypes.bool,
};

export default DynamicFieldBulkRow;
